import { UploadedFile, EUploadStatus } from "logic/src/objects/file/types";
import cls from "classnames";
import { useTranslation } from "react-i18next";
import last from "lodash/last";
import { downloadFile } from "logic/src/utils/download";
import { Image, Tooltip } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import {
  isImage,
  isImageFileName,
  getFileIconFromName,
} from "logic/src/objects/file/utils";
import { ReactComponent as FileErrorIcon } from "logic/src/components/icons/fileError.svg";
import { ReactComponent as DocLoadingIcon } from "logic/src/components/icons/docLoading.svg";
import { ReactComponent as GrayCloseIcon } from "logic/src/components/icons/gray_close.svg";
import { ReactComponent as DownloadIcon } from "logic/src/components/icons/download.svg";
import Mask from "@/components/Mask";
import CircleIcon from "@/components/icons/circle";
import styles from "./index.module.scss";

interface IProps {
  file?: File;
  className?: string;
  uploadStatus: EUploadStatus;
  fileInfo?: UploadedFile;
  onDelete?: (file?: File, fileInfo?: UploadedFile) => void;
  canDownload?: boolean;
  loading?: boolean;
  canPreview?: boolean;
  statusText?: string;
}

const FileDisplay = ({
  file,
  uploadStatus,
  fileInfo,
  className,
  onDelete,
  canDownload,
  loading,
  canPreview,
  statusText,
}: IProps) => {
  const { t } = useTranslation();
  const imageFile = file ? isImage(file) : isImageFileName(fileInfo?.file_name);
  const fileName = fileInfo?.file_name || file?.name || "";
  // 为防止文件名中有"."但不是扩展名的情况,把"."后面字符串短于7个字符的情况当做扩展名处理
  const extension =
    fileName.lastIndexOf(".") > 0 && last(fileName.split("."))!.length < 7
      ? last(fileName.split("."))
      : "";
  const realFileName = extension
    ? fileName.slice(0, Number("-" + (extension.length + 1)))
    : fileName;

  const displayFileName =
    realFileName.length > 14 ? realFileName.slice(0, 13) + "..." : realFileName;
  const uploadingMask =
    uploadStatus === EUploadStatus.uploading ? (
      <Mask className={styles.mask}>
        <CircleIcon white className={cls(styles.loadingIcon, "rotated")} />
      </Mask>
    ) : null;

  const errorMask =
    uploadStatus === EUploadStatus.error ? (
      <Mask className={styles.mask}>
        <Tooltip placement="top" title={statusText}>
          <FileErrorIcon className={styles.imageErrorIcon} />
        </Tooltip>
      </Mask>
    ) : null;

  const deleteIcon = onDelete ? (
    <GrayCloseIcon
      onClick={() => onDelete(file, fileInfo)}
      className={styles.close}
    />
  ) : null;

  const downloadIcon = canDownload && !!fileInfo?.url && (
    <DownloadIcon
      className={styles.download}
      onClick={() => downloadFile(fileInfo.url, fileInfo?.file_name)}
    />
  );

  const Icon = getFileIconFromName(fileInfo?.file_name);
  if (imageFile) {
    const src =
      uploadStatus === EUploadStatus.completed
        ? fileInfo?.url
        : file
          ? URL.createObjectURL(file)
          : "";

    return (
      <span
        className={cls(
          styles.container,
          styles.imageContainer,
          uploadStatus === EUploadStatus.uploading && styles.loadingContainer,
          className,
        )}
      >
        {errorMask}
        {uploadingMask}
        {deleteIcon}
        {canPreview ? (
          <Image
            className={styles.image}
            src={src}
            key={src}
            preview={{
              mask: (
                <div>
                  <EyeOutlined />
                </div>
              ),
            }}
          />
        ) : (
          <img className={styles.image} src={src}></img>
        )}
      </span>
    );
  } else {
    return (
      <span className={cls(styles.container, className)}>
        {deleteIcon}
        <span className={styles.fileIconBox}>
          {uploadStatus === EUploadStatus.uploading && (
            <DocLoadingIcon className={styles.fileIcon} />
          )}
          {uploadStatus === EUploadStatus.error && (
            <FileErrorIcon className={styles.fileIcon} />
          )}
          {uploadStatus === EUploadStatus.completed && (
            <Icon className={styles.fileIcon} />
          )}
        </span>
        {!loading ? (
          <div className={styles.fileBody}>
            <span className={styles.fileName}>
              <span className={styles.fileNameBody}>{realFileName}</span>
              {/* <span className={styles.fileNameExt}>
                {extension ? extension : ""}
              </span> */}
            </span>
            {statusText && (
              <span className={styles.fileStatus}>{statusText}</span>
            )}
          </div>
        ) : (
          <span className={styles.fileName}>
            <Spin
              indicator={
                <>
                  {/* <LoadingOutlined style={{ fontSize: 24 }} spin /> */}
                  {/* 文件加载中... */}
                  {t("sensechat-189")}
                </>
              }
            />
          </span>
        )}

        {downloadIcon}
      </span>
    );
  }
};

export default FileDisplay;
